import { useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { Plus, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { memberColor } from './member-color'
import { WEEKDAYS } from './rotation-constants'
import { suggestMember } from './suggest-member'

export type RotationMember = { id: string; name: string }

export interface RotationGridProps {
  /** Monday (yyyy-MM-dd) for each week to render, top-to-bottom. */
  weeks: string[]
  /** date (yyyy-MM-dd) -> assigned staff id (null/absent = unassigned). */
  assignmentsByDate: Record<string, string | null>
  /** role members, in order — index drives each member's color. */
  members: RotationMember[]
  /** today (yyyy-MM-dd) — that cell is marked. */
  todayIso: string
  /** dates before this (yyyy-MM-dd) are read-only + grayed (the past). When
   *  omitted, all cells are editable. */
  minEditableDate?: string
  /** editable mode: called when a day's assignee changes (null = clear). */
  onAssign?: (date: string, staffId: string | null) => void
  /** readonly preview: date -> raw CSV name that didn't resolve to a member. */
  unknownByDate?: Record<string, string>
  /** name of the calendar-wide fallback person, for the blank-day hint. */
  fallbackName?: string
  /** ref attached to the week row whose Monday === todayWeekMonday (for
   *  scroll-to-today in a scrolling container). */
  todayWeekRef?: React.Ref<HTMLTableRowElement>
  todayWeekMonday?: string
}

export default function RotationGrid(props: RotationGridProps) {
  const {
    weeks,
    assignmentsByDate,
    members,
    todayIso,
    minEditableDate,
    onAssign,
    unknownByDate = {},
    fallbackName,
    todayWeekRef,
    todayWeekMonday,
  } = props

  const [openCellDate, setOpenCellDate] = useState<string | null>(null)

  const memberById = useMemo(() => {
    const map = new Map<string, { member: RotationMember; index: number }>()
    members.forEach((member, index) => map.set(member.id, { member, index }))
    return map
  }, [members])

  const daysFor = (mondayIso: string): string[] => {
    const monday = DateTime.fromISO(mondayIso)
    return Array.from(
      { length: 7 },
      (_, i) => monday.plus({ days: i }).toISODate() ?? '',
    )
  }

  const anyBlank = useMemo(
    () =>
      weeks
        .flatMap(daysFor)
        .some(date => !assignmentsByDate[date] && !unknownByDate[date]),
    [weeks, assignmentsByDate, unknownByDate],
  )

  return (
    <div className="space-y-3">
      {/* Horizontal scrolling is owned by the parent scroll container (so its
          x-scrollbar is reachable and the sticky header pins correctly). */}
      <div>
        <table className="w-full min-w-[640px] table-fixed border-separate border-spacing-1 text-sm">
          <thead className="sticky top-0 z-10 bg-background">
            <tr>
              {WEEKDAYS.map(label => (
                <th
                  key={label}
                  // after: draws the divider under the sticky header — a plain
                  // border-b would break at every border-spacing gap, so each
                  // cell's line is extended sideways to bridge the gaps.
                  className="relative px-2 py-1.5 text-center text-xs font-medium text-muted-foreground after:absolute after:-inset-x-1 after:bottom-0 after:h-px after:bg-border"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map(mondayIso => {
              return (
                <tr
                  key={mondayIso}
                  data-week={mondayIso}
                  ref={mondayIso === todayWeekMonday ? todayWeekRef : undefined}
                >
                  {daysFor(mondayIso).map(date => (
                    <td key={date} className="p-0 align-top">
                      <DayCell
                        date={date}
                        staffId={assignmentsByDate[date] ?? null}
                        unknownName={unknownByDate[date]}
                        isToday={date === todayIso}
                        isPast={!!minEditableDate && date < minEditableDate}
                        members={members}
                        memberById={memberById}
                        open={openCellDate === date}
                        onOpenChange={o => setOpenCellDate(o ? date : null)}
                        onAssign={staffId => {
                          onAssign?.(date, staffId)
                          setOpenCellDate(null)
                        }}
                      />
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {anyBlank && fallbackName && (
        <p className="text-xs text-muted-foreground">
          Blank days fall back to{' '}
          <span className="font-medium">{fallbackName}</span>.
        </p>
      )}
    </div>
  )
}

function DayCell(props: {
  date: string
  staffId: string | null
  unknownName?: string
  isToday: boolean
  isPast: boolean
  members: RotationMember[]
  memberById: Map<string, { member: RotationMember; index: number }>
  open: boolean
  onOpenChange: (open: boolean) => void
  onAssign: (staffId: string | null) => void
}) {
  const {
    date,
    staffId,
    unknownName,
    isToday,
    isPast,
    members,
    memberById,
    open,
    onOpenChange,
    onAssign,
  } = props

  const day = DateTime.fromISO(date)
  // Like Google Calendar, the 1st of a month is labeled with the month.
  const dayLabel = day.day === 1 ? day.toFormat('MMM d') : String(day.day)
  const resolved = staffId ? memberById.get(staffId) : undefined
  const isError = !!unknownName && !resolved
  const suggestion = isError ? suggestMember(unknownName!, members) : null
  const suggested = suggestion
    ? members.find(m => m.name === suggestion)
    : undefined

  // Past cells are locked (read-only) — you can't rewrite a day that already
  // happened. Every other cell is editable (assign someone, or fix a
  // CSV-preview error cell).
  const interactive = !isPast

  // "Today" is marked by a black rounded box around the day-number (white text);
  // past is grayed out.
  const base = cn(
    'relative flex h-14 w-full flex-col justify-between rounded-md border bg-card px-2 py-1 text-left transition-colors',
    isError && 'border-destructive/40 bg-destructive/10',
    isPast && 'bg-muted/40 opacity-60',
    interactive &&
      'cursor-pointer hover:border-foreground/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground',
  )

  const cellBody = (
    <>
      {isToday ? (
        // Wrapped in a black box, offset so the digit stays where it sits
        // without the box (no layout shift vs. a plain day number).
        <span className="-ml-1 -mt-0.5 self-start whitespace-nowrap rounded-md bg-foreground px-1 py-0.5 text-[10px] font-semibold tabular-nums text-background">
          {dayLabel}
        </span>
      ) : (
        <span className="whitespace-nowrap text-[10px] tabular-nums text-muted-foreground">
          {dayLabel}
        </span>
      )}
      {resolved ? (
        <span className="flex items-center gap-1.5 overflow-hidden">
          <span
            className={cn(
              'block h-2.5 w-2.5 flex-none rounded-full',
              memberColor(resolved.index).swatch,
            )}
          />
          <span className="truncate text-xs font-medium">
            {resolved.member.name}
          </span>
        </span>
      ) : isError ? (
        <span className="flex items-center gap-1 overflow-hidden text-xs text-destructive">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span className="truncate">{unknownName}</span>
        </span>
      ) : !isPast ? (
        <span className="flex items-center justify-center text-muted-foreground">
          <Plus className="h-4 w-4" />
        </span>
      ) : (
        <span className="text-xs text-muted-foreground/60">—</span>
      )}
    </>
  )

  if (!interactive) {
    return <div className={base}>{cellBody}</div>
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" className={base}>
          {cellBody}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-1">
        {members.length === 0 && (
          <p className="px-2 py-2 text-xs text-muted-foreground">
            No members to assign yet — add people in Members above.
          </p>
        )}
        {isError && (
          <div className="mb-1 border-b pb-1">
            <p className="truncate px-2 py-1 text-xs text-muted-foreground">
              {suggested
                ? `"${unknownName}" isn't a member — did you mean:`
                : `"${unknownName}" isn't a member`}
            </p>
            {suggested && (
              <button
                type="button"
                onClick={() => onAssign(suggested.id)}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
              >
                <span
                  className={cn(
                    'h-2.5 w-2.5 shrink-0 rounded-full',
                    memberColor(memberById.get(suggested.id)?.index ?? 0)
                      .swatch,
                  )}
                />
                <span className="flex-1 truncate font-medium">
                  {suggested.name}
                </span>
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                  Suggested
                </span>
              </button>
            )}
          </div>
        )}
        <div className="max-h-56 overflow-y-auto">
          {members.map((member, index) => {
            // The suggested member is already shown at the top — don't repeat.
            if (suggested && member.id === suggested.id) return null
            return (
              <button
                key={member.id}
                type="button"
                onClick={() => onAssign(member.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted',
                  staffId === member.id && 'bg-muted',
                )}
              >
                <span
                  className={cn(
                    'h-2.5 w-2.5 shrink-0 rounded-full',
                    memberColor(index).swatch,
                  )}
                />
                <span className="truncate">{member.name}</span>
              </button>
            )
          })}
          {(staffId || isError) && (
            <button
              type="button"
              onClick={() => onAssign(null)}
              className="mt-1 w-full rounded px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-muted"
            >
              Clear day
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
